import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  User,
  Users,
  Ticket,
  Check,
  XCircle,
  Mail,
  Phone,
  X,
  Maximize2,
  CalendarDays,
  Sparkles,
  CircleDot,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import DocumentRequiredModal from "../../components/common/DocumentRequiredModal";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import Footer from "../../components/landing/Footer";
import ConferenceCard from "../../components/media_and_events/ConferenceCard";
import RegistrationDrawer from "../../components/media_and_events/RegistrationDrawer";

export default function EventDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const isDocumentComplete = useMemo(() => {
    if (!user) return false;
    return Boolean(
      user.document_type &&
        user.card_number &&
        user.issue_date &&
        user.expiry_date &&
        user.id_card_front &&
        user.id_card_back
    );
  }, [user]);

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!slug) {
        toast.error("Event not found");
        navigate("/conference-trade-mission");
        return;
      }

      setLoading(true);
      try {
        const response = await conferenceAndTradeMissionService.getEventDetails(
          slug
        );

        if (response.data?.status_code === 200 && response.data?.data?.event) {
          setEventData(response.data.data);
        } else {
          toast.error(response.data.reason || "Failed to load event details");
          navigate("/conference-trade-mission");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.reason ||
            "An error occurred while fetching event details"
        );
        navigate("/conference-trade-mission");
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [slug, navigate]);

  // Handle keyboard navigation and close modal
  useEffect(() => {
    if (isImageModalOpen) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          setIsImageModalOpen(false);
        } else if (e.key === "ArrowLeft" && eventData?.event) {
          const images = eventData.event.featured_images?.length
            ? [eventData.event.thumbnail, ...eventData.event.featured_images]
            : [eventData.event.thumbnail];
          if (images.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
          }
        } else if (e.key === "ArrowRight" && eventData?.event) {
          const images = eventData.event.featured_images?.length
            ? [eventData.event.thumbnail, ...eventData.event.featured_images]
            : [eventData.event.thumbnail];
          if (images.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isImageModalOpen, eventData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col gap-4 items-center">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-b-2 border-emerald-500 animate-spin"></div>
            <div className="absolute inset-0 w-12 h-12 rounded-full border-2 border-emerald-200 animate-pulse"></div>
          </div>
          <p className="text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!eventData?.event) {
    return null;
  }

  const event = eventData.event;
  const similarEvents = eventData.similar_event || [];

  // Get all event types
  const eventTypes = event.event_type || [];
  const hasMultipleTypes = eventTypes.length > 1;
  const isConference = eventTypes.some((type) =>
    type?.toLowerCase().includes("conference")
  );
  const isTradeMission = eventTypes.some(
    (type) =>
      type?.toLowerCase().includes("trade") ||
      type?.toLowerCase().includes("mission")
  );

  // Format event type name
  const formatEventTypeName = (type) => {
    if (!type) return "Event";
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Get color scheme for event type
  const getEventTypeColor = (type) => {
    const normalizedType = type?.toLowerCase() || "";

    if (normalizedType.includes("conference")) {
      return "bg-gradient-to-r from-blue-500 to-blue-600";
    }
    if (
      normalizedType.includes("trade") ||
      normalizedType.includes("mission")
    ) {
      return "bg-gradient-to-r from-purple-500 to-purple-600";
    }
    if (
      normalizedType.includes("workshop") ||
      normalizedType.includes("seminar")
    ) {
      return "bg-gradient-to-r from-orange-500 to-orange-600";
    }
    if (
      normalizedType.includes("exhibition") ||
      normalizedType.includes("expo")
    ) {
      return "bg-gradient-to-r from-pink-500 to-pink-600";
    }
    if (
      normalizedType.includes("festival") ||
      normalizedType.includes("celebration")
    ) {
      return "bg-gradient-to-r from-yellow-500 to-yellow-600";
    }
    if (normalizedType.includes("summit") || normalizedType.includes("forum")) {
      return "bg-gradient-to-r from-indigo-500 to-indigo-600";
    }
    return "bg-gradient-to-r from-emerald-500 to-emerald-600";
  };

  // Get badge color for inline badges
  const getEventTypeBadgeColor = (type) => {
    const normalizedType = type?.toLowerCase() || "";

    if (normalizedType.includes("conference")) {
      return "text-blue-700 bg-blue-50 border-blue-200";
    }
    if (
      normalizedType.includes("trade") ||
      normalizedType.includes("mission")
    ) {
      return "text-purple-700 bg-purple-50 border-purple-200";
    }
    if (
      normalizedType.includes("workshop") ||
      normalizedType.includes("seminar")
    ) {
      return "text-orange-700 bg-orange-50 border-orange-200";
    }
    if (
      normalizedType.includes("exhibition") ||
      normalizedType.includes("expo")
    ) {
      return "text-pink-700 bg-pink-50 border-pink-200";
    }
    if (
      normalizedType.includes("festival") ||
      normalizedType.includes("celebration")
    ) {
      return "text-yellow-700 bg-yellow-50 border-yellow-200";
    }
    if (normalizedType.includes("summit") || normalizedType.includes("forum")) {
      return "text-indigo-700 bg-indigo-50 border-indigo-200";
    }
    return "text-emerald-700 bg-emerald-50 border-emerald-200";
  };

  // Create Google Maps embed URL from venue address or link
  const getMapIframeUrl = () => {
    if (!event.venue_google_location) {
      // If no Google location link, create from address
      const address = `${event.venue_street_address || ""}, ${
        event.city || ""
      }, ${event.country || ""}`
        .trim()
        .replace(/^,\s*|,\s*$/g, "");
      if (!address) return null;
      const encodedAddress = encodeURIComponent(address);
      return `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
    }

    // If it's already an embed URL, return it
    if (event.venue_google_location.includes("maps/embed")) {
      return event.venue_google_location;
    }

    // If it's a maps.app.goo.gl or maps.google.com link, convert to embed format
    // Extract place ID or use address
    if (event.venue_google_location.includes("maps.app.goo.gl")) {
      // For short links, we'll use the address instead
      const address = `${event.venue_street_address || ""}, ${
        event.city || ""
      }, ${event.country || ""}`
        .trim()
        .replace(/^,\s*|,\s*$/g, "");
      if (!address) return null;
      const encodedAddress = encodeURIComponent(address);
      return `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
    }

    // For regular maps.google.com links, try to extract location or use address
    const address = `${event.venue_street_address || ""}, ${
      event.city || ""
    }, ${event.country || ""}`
      .trim()
      .replace(/^,\s*|,\s*$/g, "");
    if (!address) return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
  };

  const mapEmbedUrl = getMapIframeUrl();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const allImages = event.featured_images?.length
    ? [event.thumbnail, ...event.featured_images]
    : [event.thumbnail];

  const eventStartDate = event.event_start_date || event.event_date;
  const eventEndDate = event.event_end_date || event.event_date;
  const eventDate =
    eventStartDate && eventEndDate && eventStartDate !== eventEndDate
      ? `${formatDate(eventStartDate)} – ${formatDate(eventEndDate)}`
      : formatDate(eventStartDate || event.event_date);
  const eventStartTime = event.event_start_time || event.event_time;
  const eventEndTime = event.event_end_time || event.event_time;
  const eventTime =
    eventStartTime && eventEndTime && eventStartTime !== eventEndTime
      ? `${formatTime(eventStartTime)} – ${formatTime(eventEndTime)}`
      : formatTime(eventStartTime || event.event_time);
  const isEventClosed = event.event_state === "closed";

  // Format currency symbol
  const formatCurrency = (currency) => {
    if (!currency) return "$";
    const currencyUpper = currency.toUpperCase();
    if (currencyUpper === "USD") return "$";
    if (currencyUpper === "GHS") return "₵";
    return currency; // Return as-is if not recognized
  };

  // Price range for conferences (min – max when multiple tiers; for mix of free + paid, use lowest paid price, not 0)
  const ticketTypes = event.ticket_types || [];
  const hasTicketTypes = isConference && ticketTypes.length > 0;
  const prices = hasTicketTypes ? ticketTypes.map((t) => parseFloat(t.price) || 0) : [];
  const minPrice = hasTicketTypes ? Math.min(...prices) : null;
  const maxPrice = hasTicketTypes ? Math.max(...prices) : null;
  const paidPrices = hasTicketTypes ? prices.filter((p) => p > 0) : [];
  const minPaidPrice = paidPrices.length > 0 ? Math.min(...paidPrices) : null;
  const minPriceTicket = hasTicketTypes ? ticketTypes.find((t) => (parseFloat(t.price) || 0) === minPrice) : null;
  const minPaidPriceTicket = hasTicketTypes && minPaidPrice != null ? ticketTypes.find((t) => (parseFloat(t.price) || 0) === minPaidPrice) : null;
  const maxPriceTicket = hasTicketTypes ? ticketTypes.find((t) => (parseFloat(t.price) || 0) === maxPrice) : null;
  const allFree = hasTicketTypes && prices.every((p) => p === 0);
  const singleTier = ticketTypes.length === 1;

  // Use overall_slots directly from event response
  const overallSlots = event.overall_slots || null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + allImages.length) % allImages.length
    );
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <AnimatePresence>
        {showDocumentModal && (
          <DocumentRequiredModal
            isOpen={showDocumentModal}
            onClose={() => setShowDocumentModal(false)}
            onGoToProfile={() => {
              setShowDocumentModal(false);
              navigate("/settings");
            }}
            title="Documents Required"
            message="Please upload your required documents in your profile before booking tickets."
          />
        )}
      </AnimatePresence>
      <div className="px-4 pt-16 pb-8 mt-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          {/* Back Button - Always at top */}
          <motion.button
            onClick={() => {
              const isTradeOrConference = isTradeMission || isConference;
              navigate(
                isTradeOrConference
                  ? "/conference-trade-mission"
                  : "/events"
              );
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -4, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex gap-2 items-center mb-6 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-lg border-2 border-gray-300 shadow-md transition-all hover:bg-gray-50 hover:border-orange-400 hover:shadow-lg"
          >
            <ChevronLeft size={18} />
            Back to Events
          </motion.button>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            {/* Left Column - Main Content */}
            <div className="space-y-12 lg:col-span-2">
              {/* Event Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="overflow-hidden relative rounded-2xl shadow-xl"
              >
                {/* Image Carousel */}
                <div className="overflow-hidden relative h-64 bg-gradient-to-br from-emerald-100 via-blue-100 to-purple-100 sm:h-80 lg:h-[500px]">
                  <div className="absolute inset-0 z-10 bg-gradient-to-t to-transparent from-black/60 via-black/20" />
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentImageIndex}
                      src={allImages[currentImageIndex]}
                      alt={event.event_title || "Event"}
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      className="object-cover w-full h-full transition-transform cursor-pointer hover:scale-105"
                      onClick={() => setIsImageModalOpen(true)}
                    />
                  </AnimatePresence>

                  {/* View Full Image Button */}
                  <motion.button
                    onClick={() => setIsImageModalOpen(true)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="flex absolute right-4 bottom-4 z-20 gap-2 items-center px-4 py-2 text-sm font-semibold text-white rounded-lg border backdrop-blur-md transition-all bg-black/50 border-white/20 hover:bg-black/70 hover:border-white/40"
                  >
                    <Maximize2 size={18} />
                    <span className="hidden sm:inline">View Full Image</span>
                  </motion.button>

                  {/* Navigation Arrows */}
                  {allImages.length > 1 && (
                    <>
                      <motion.button
                        onClick={prevImage}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="flex absolute left-4 top-1/2 z-20 justify-center items-center w-12 h-12 text-white rounded-full shadow-lg backdrop-blur-md transition-all bg-black/40 hover:bg-black/60"
                      >
                        <ChevronLeft size={22} />
                      </motion.button>
                      <motion.button
                        onClick={nextImage}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="flex absolute right-4 top-1/2 z-20 justify-center items-center w-12 h-12 text-white rounded-full shadow-lg backdrop-blur-md transition-all bg-black/40 hover:bg-black/60"
                      >
                        <ChevronRight size={22} />
                      </motion.button>
                    </>
                  )}

                  {/* Image Dots */}
                  {allImages.length > 1 && (
                    <div className="flex absolute bottom-4 left-1/2 gap-2 -translate-x-1/2">
                      {allImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`h-2 rounded-full transition-all ${
                            currentImageIndex === index
                              ? "w-8 bg-white"
                              : "w-2 bg-white/50 hover:bg-white/75"
                          }`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Event Type Badges */}
                  <div className="absolute top-4 left-4 z-20">
                    <div className="flex flex-wrap gap-2">
                      {eventTypes.length > 0 ? (
                        eventTypes
                          .slice(0, hasMultipleTypes ? 2 : 1)
                          .map((type, index) => (
                            <motion.span
                              key={`${type}-${index}`}
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.2 + index * 0.1 }}
                              className={`inline-flex px-4 py-1.5 text-xs font-bold text-white rounded-full backdrop-blur-md shadow-lg ${getEventTypeColor(
                                type
                              )}`}
                            >
                              {formatEventTypeName(type)}
                            </motion.span>
                          ))
                      ) : (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2 }}
                          className="inline-flex px-4 py-1.5 text-xs font-bold text-white rounded-full backdrop-blur-md shadow-lg bg-gradient-to-r from-gray-500 to-gray-600"
                        >
                          Event
                        </motion.span>
                      )}
                      {hasMultipleTypes && eventTypes.length > 2 && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.4 }}
                          className="inline-flex px-4 py-1.5 text-xs font-bold text-white rounded-full backdrop-blur-md shadow-lg bg-gradient-to-r from-gray-600 to-gray-700"
                          title={eventTypes
                            .slice(2)
                            .map(formatEventTypeName)
                            .join(", ")}
                        >
                          +{eventTypes.length - 2}
                        </motion.span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Banner Content */}
                <div className="relative z-10 p-6 bg-white sm:p-8 lg:p-10">
                  <div className="flex flex-wrap gap-3 items-center mb-4">
                    {eventTypes.length > 0 ? (
                      eventTypes.map((type, index) => (
                        <motion.span
                          key={`${type}-${index}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className={`px-4 py-1.5 text-xs font-bold rounded-full border ${getEventTypeBadgeColor(
                            type
                          )}`}
                        >
                          {formatEventTypeName(type)}
                        </motion.span>
                      ))
                    ) : (
                      <motion.span
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="px-4 py-1.5 text-xs font-bold text-gray-700 bg-gray-50 border border-gray-200 rounded-full"
                      >
                        Event
                      </motion.span>
                    )}
                    {hasTicketTypes && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className={`px-4 py-1.5 text-lg font-bold rounded-lg ${
                          allFree
                            ? "bg-gradient-to-r from-green-500 to-green-600 text-white"
                            : "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"
                        }`}
                      >
                        {allFree ? (
                          "FREE"
                        ) : singleTier ? (
                          <>
                            {formatCurrency(minPriceTicket?.currency)}
                            {minPrice}
                          </>
                        ) : (
                          <>
                            {formatCurrency(minPaidPriceTicket?.currency ?? maxPriceTicket?.currency)}
                            {minPaidPrice ?? minPrice} – {formatCurrency(maxPriceTicket?.currency)}
                            {maxPrice}
                          </>
                        )}
                      </motion.span>
                    )}
                  </div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-6 text-2xl font-extrabold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 sm:text-3xl lg:text-4xl"
                  >
                    {event.event_title || "Untitled Event"}
                  </motion.h1>

                  {/* Event Info */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex flex-wrap gap-6 mb-8 text-sm"
                  >
                    <div className="flex gap-2 items-center px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-100">
                      <Calendar className="text-emerald-600" size={18} />
                      <span className="font-semibold text-gray-900">
                        {eventDate}
                      </span>
                      <span className="text-gray-400">•</span>
                      <Clock className="text-emerald-600" size={18} />
                      <span className="text-gray-700">{eventTime}</span>
                    </div>
                    <div className="flex gap-2 items-center px-4 py-2 bg-blue-50 rounded-lg border border-blue-100">
                      <MapPin className="text-blue-600" size={18} />
                      <span className="font-semibold text-gray-900">
                        {event.venue_name}
                      </span>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-wrap gap-4 items-center"
                  >
                    {isEventClosed ? (
                      <div className="flex gap-3 items-center px-6 py-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl border-2 border-gray-300 shadow-md">
                        <XCircle size={24} className="text-gray-500" />
                        <div>
                          <p className="text-base font-bold text-gray-700">
                            Event Closed
                          </p>
                          <p className="text-sm text-gray-600">
                            Registration for this event has been closed
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <motion.button
                          onClick={() => {
                            if (!isEventClosed) {
                              // Check if user is authenticated
                              if (!isAuthenticated) {
                                // Redirect to signin with current path as return destination
                                navigate("/signin", {
                                  state: { from: `/event-details/${slug}` },
                                });
                                toast.info(
                                  "Please sign in to register for this event"
                                );
                                return;
                              }

                              if (!isDocumentComplete) {
                                setShowDocumentModal(true);
                                return;
                              }

                              // User is authenticated, proceed with registration
                              if (isConference) {
                                // Conferences: require ticket/participation category selection first
                                setIsDrawerOpen(true);
                                return;
                              }
                              if (isTradeMission) {
                                // Trade missions: use existing registration flow
                                navigate(`/event-details/${slug}/register`);
                              } else {
                                // For general events, clear any existing booking data and start fresh
                                sessionStorage.removeItem(
                                  `eventBooking_${slug}`
                                );
                                sessionStorage.removeItem(
                                  `eventBookingSuccess_${slug}`
                                );

                                // Use new ticket booking flow
                                navigate(`/event-details/${slug}/book`, {
                                  state: { eventData: event },
                                });
                              }
                            }
                          }}
                          disabled={isEventClosed}
                          whileHover={!isEventClosed ? { scale: 1.05 } : {}}
                          whileTap={!isEventClosed ? { scale: 0.95 } : {}}
                          className={`px-8 py-4 text-base font-bold text-white rounded-xl shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            isEventClosed
                              ? "bg-gradient-to-r from-gray-400 to-gray-500 opacity-60 cursor-not-allowed"
                              : isConference || isTradeMission
                              ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 focus:ring-orange-500"
                              : "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 focus:ring-emerald-500"
                          }`}
                        >
                          {isConference || isTradeMission
                            ? "Register now"
                            : "Book Tickets"}
                        </motion.button>
                        {overallSlots !== null && (
                          <div className="flex gap-2 items-center px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                            <Users size={16} className="text-emerald-600" />
                            <span className="text-sm font-medium text-gray-700">
                              {overallSlots}{" "}
                              {isConference ? "seats" : "slots"} available
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </motion.div>
                </div>
              </motion.div>

              {/* About the Event */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div className="absolute top-0 bottom-0 -left-4 w-1 bg-gradient-to-b from-emerald-400 to-emerald-600 rounded-full" />
                <h2 className="mb-6 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                  About the Event
                </h2>
                <p className="text-lg leading-relaxed text-gray-700 whitespace-pre-line">
                  {event.about_event || "No description available."}
                </p>
              </motion.div>

              {/* Ticket Types - Only for Conferences */}
              {isConference && event.ticket_types?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                    Ticket Options
                  </h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {event.ticket_types.map((ticket, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -4 }}
                        className="relative p-8 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 transition-all hover:border-emerald-400 hover:shadow-xl"
                      >
                        {/* Ticket Badge */}
                        <div className="absolute top-4 right-4">
                          <Ticket
                            size={28}
                            className="text-emerald-500 opacity-30"
                          />
                        </div>

                        {/* Ticket Name */}
                        <h3 className="mb-3 text-xl font-bold text-gray-900">
                          {ticket.name}
                        </h3>

                        {/* Price: FREE (no currency) when 0, otherwise currency + price */}
                        <div className="mb-6">
                          {ticket.price === 0 || parseFloat(ticket.price) === 0 ? (
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg">
                              <span className="text-2xl font-extrabold text-white">FREE</span>
                              <Check size={24} className="text-white" />
                            </div>
                          ) : (
                            <span className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500">
                              {formatCurrency(ticket.currency)}
                              {ticket.price}
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        {ticket.description && (
                          <p className="mb-4 text-sm leading-relaxed text-gray-600">
                            {ticket.description}
                          </p>
                        )}

                        {/* Slots Available */}
                        {ticket.slots_allocated !== null && (
                          <div className="flex gap-2 items-center mb-4 text-sm text-gray-600">
                            <Users size={16} className="text-emerald-600" />
                            <span>
                              {ticket.slots_allocated} slot
                              {ticket.slots_allocated !== 1 ? "s" : ""}{" "}
                              allocated
                            </span>
                          </div>
                        )}

                        {/* What's Included */}
                        {ticket.whats_included?.length > 0 && (
                          <div className="pt-4 mb-4 border-t border-gray-200">
                            <p className="mb-3 text-xs font-semibold tracking-wide text-gray-700 uppercase">
                              What's Included
                            </p>
                            <ul className="space-y-2">
                              {ticket.whats_included.map((item, idx) => (
                                <li
                                  key={idx}
                                  className="flex gap-2 items-start text-sm text-gray-600"
                                >
                                  <Check
                                    size={16}
                                    className="flex-shrink-0 mt-0.5 text-emerald-600"
                                  />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Performers/Entertainers */}
              {event.performers &&
                Array.isArray(event.performers) &&
                event.performers.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5 }}
                  >
                    <h2 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                      {isConference || isTradeMission
                        ? "Speakers"
                        : "Performers / Entertainers"}
                    </h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {event.performers.map((speaker, index) => {
                        const name = typeof speaker === "string" ? speaker : speaker?.name;
                        const image = typeof speaker === "object" && speaker?.image ? speaker.image : null;
                        return (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ x: 4 }}
                            className="flex gap-4 items-center p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-100 transition-all hover:shadow-md"
                          >
                            {image ? (
                              <img src={image} alt={name} className="flex-shrink-0 w-14 h-14 rounded-full object-cover shadow-lg" />
                            ) : (
                              <div className="flex flex-shrink-0 justify-center items-center w-14 h-14 font-bold text-white bg-gradient-to-br from-emerald-500 via-emerald-600 to-blue-600 rounded-full shadow-lg">
                                <User size={24} />
                              </div>
                            )}
                            <p className="text-lg font-semibold text-gray-900">{name}</p>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

              {/* Sponsors/Partners */}
              {event.sponsors?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                    Sponsors/Partners
                  </h2>
                  <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-4">
                    {event.sponsors.map((sponsor, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.1, y: -4 }}
                        className="flex justify-center items-center p-6 bg-white rounded-xl border border-gray-200 transition-all hover:border-emerald-300 hover:shadow-lg"
                      >
                        <img
                          src={sponsor.logo}
                          alt={sponsor.name}
                          className="object-contain max-w-full max-h-20 transition-all sm:max-h-24 lg:max-h-28"
                        />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              {/* Organizer */}
              {event.organizer && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
                >
                  <h3 className="mb-6 text-lg font-bold text-gray-900">
                    Organizer
                  </h3>

                  {/* Organizer Info */}
                  <div className="mb-6 space-y-4">
                    {/* Organizer Logo/Icon and Name */}
                    <div className="flex gap-4 items-start">
                      {typeof event.organizer === "object" &&
                      event.organizer.logo ? (
                        <div className="flex-shrink-0">
                          <img
                            src={event.organizer.logo}
                            alt={event.organizer.name || "Organizer"}
                            className="object-cover w-16 h-16 rounded-xl border-2 border-gray-200 shadow-md"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextElementSibling.style.display =
                                "flex";
                            }}
                          />
                          <div className="hidden justify-center items-center w-16 h-16 text-xl font-bold text-white bg-emerald-600 rounded-xl shadow-lg">
                            {(event.organizer.name || "OR")
                              .substring(0, 2)
                              .toUpperCase()}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-shrink-0 justify-center items-center w-16 h-16 text-xl font-bold text-white bg-emerald-600 rounded-xl shadow-lg">
                          {typeof event.organizer === "object"
                            ? (event.organizer.name || "OR")
                                .substring(0, 2)
                                .toUpperCase()
                            : event.organizer?.substring(0, 2).toUpperCase() ||
                              "OR"}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-lg font-bold text-gray-900">
                          {typeof event.organizer === "object"
                            ? event.organizer.name || "Event Organizer"
                            : "Event Organizer"}
                        </h4>
                        {typeof event.organizer === "object" &&
                          event.organizer.city &&
                          event.organizer.country && (
                            <div className="flex gap-1 items-center text-sm text-gray-600">
                              <MapPin size={14} className="text-emerald-600" />
                              <span>
                                {event.organizer.city},{" "}
                                {event.organizer.country}
                              </span>
                            </div>
                          )}
                        {event.point_in_time && (
                          <p className="mt-1 text-xs text-gray-500">
                            Posted on {formatDate(event.point_in_time)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Organizer Contact Details */}
                    <div className="pt-4 space-y-3 border-t border-gray-200">
                      <motion.a
                        href="mailto:info@tripsecuregh.com?subject=Inquiry about Event"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex gap-3 justify-center items-center px-4 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg shadow-sm transition-all hover:bg-emerald-700 hover:shadow-md"
                      >
                        <Mail size={18} />
                        <span>Email Event Team</span>
                      </motion.a>
                      
                      <motion.a
                        href="tel:+233204014370"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex gap-3 justify-center items-center px-4 py-3 text-sm font-medium text-emerald-700 bg-white rounded-lg border-2 border-emerald-600 shadow-sm transition-all hover:bg-emerald-50"
                      >
                        <Phone size={18} />
                        <span>Call Event Team</span>
                      </motion.a>
                      
                      <div className="pt-3 mt-3 space-y-2 text-xs text-gray-600 border-t border-gray-100">
                        <div className="flex gap-2 items-center">
                          <Mail size={14} className="text-gray-400" />
                          <span>info@tripsecuregh.com</span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Phone size={14} className="text-gray-400" />
                          <span>+233 (0)20 401 4370</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </motion.div>
              )}

              {/* Venue */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
              >
                <h3 className="mb-6 text-lg font-bold text-gray-900">Venue</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="mb-2 font-bold text-gray-900">
                      {event.venue_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {event.venue_street_address}
                      {event.city && `, ${event.city}`}
                      {event.country && `, ${event.country}`}
                    </p>
                  </div>

                  {/* Google Maps Embed */}
                  {mapEmbedUrl && (
                    <div className="overflow-hidden rounded-xl border-2 border-gray-200 shadow-md">
                      <iframe
                        src={mapEmbedUrl}
                        width="100%"
                        height="220"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Map of ${event.venue_name}`}
                        className="w-full"
                      />
                    </div>
                  )}

                  {event.venue_google_location && (
                    <motion.a
                      href={event.venue_google_location}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md transition-all hover:from-orange-600 hover:to-orange-700"
                    >
                      <ExternalLink size={16} />
                      Get directions
                    </motion.a>
                  )}
                </div>
              </motion.div>

              {/* Event Schedule */}
              {(() => {
                const scheduleData = event.agenda?.length > 0 ? event.agenda : event.itinerary?.length > 0 ? event.itinerary : null;
                
                if (!scheduleData) return null;
                
                return (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="overflow-hidden p-6 bg-gradient-to-br from-white to-white rounded-2xl border border-emerald-200 shadow-lg via-emerald-50/30"
                  >
                    {/* Header */}
                    <div className="flex gap-3 items-center mb-6">
                      <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-md">
                        <CalendarDays size={20} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Event Schedule
                        </h3>
                        <p className="text-xs font-medium text-emerald-600">
                          {scheduleData.length} {scheduleData.length === 1 ? 'activity' : 'activities'} planned
                        </p>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="relative">
                      {/* Vertical Timeline Line */}
                      <div className="absolute left-[23px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-300 via-emerald-400 to-emerald-300" />
                      
                      <div className="space-y-6">
                        {scheduleData.map((item, index) => {
                          const isFirst = index === 0;
                          const isLast = index === scheduleData.length - 1;
                        
                        return (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ delay: index * 0.1 }}
                            className="relative"
                          >
                            {/* Timeline Node */}
                            <div className="flex gap-4 items-start">
                              <div className="relative flex-shrink-0">
                                {/* Glowing Dot */}
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: index * 0.1 + 0.3, type: "spring" }}
                                  className="relative z-10"
                                >
                                  <div className="flex justify-center items-center w-12 h-12 bg-white rounded-full border-4 border-emerald-500 shadow-lg">
                                    {isFirst ? (
                                      <Sparkles size={18} className="text-emerald-600" />
                                    ) : (
                                      <CircleDot size={18} className="text-emerald-600" />
                                    )}
                                  </div>
                                  {/* Glow effect */}
                                  <div className="absolute inset-0 bg-emerald-400 rounded-full opacity-20 animate-pulse" />
                                </motion.div>
                              </div>

                              {/* Content Card */}
                              <motion.div
                                whileHover={{ x: 4, scale: 1.01 }}
                                className="flex-1 p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all hover:border-emerald-400 hover:shadow-md"
                              >
                                {/* Time Badge */}
                                <div className="flex gap-2 items-center mb-3">
                                  <div className="inline-flex gap-2 items-center px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg shadow-sm">
                                    <Clock size={14} className="text-white" />
                                    <span className="text-xs font-bold text-white">
                                      {item.time}
                                    </span>
                                  </div>
                                  {isFirst && (
                                    <span className="inline-flex px-2 py-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-md">
                                      START
                                    </span>
                                  )}
                                  {isLast && (
                                    <span className="inline-flex px-2 py-1 text-[10px] font-bold text-orange-700 bg-orange-100 rounded-md">
                                      END
                                    </span>
                                  )}
                                </div>

                                {/* Title */}
                                <h4 className="mb-2 text-base font-bold leading-tight text-gray-900">
                                  {item.title}
                                </h4>

                                {/* Description */}
                                {item.description && (
                                  <p className="text-sm leading-relaxed text-gray-600">
                                    {item.description}
                                  </p>
                                )}

                                {/* Decorative Corner */}
                                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-emerald-50 to-transparent rounded-xl opacity-50" />
                              </motion.div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                    {/* Footer Note */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="flex gap-2 items-center pt-4 mt-6 border-t border-emerald-100"
                    >
                      <div className="flex justify-center items-center w-6 h-6 bg-emerald-100 rounded-full">
                        <Sparkles size={12} className="text-emerald-600" />
                      </div>
                      <p className="text-xs italic text-gray-600">
                        Schedule may be subject to change
                      </p>
                    </motion.div>
                  </motion.div>
                );
              })()}
            </div>
          </div>

          {/* Similar Events */}
          {similarEvents.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="pt-12 mt-16 border-t-2 border-gray-200"
            >
              <h3 className="mb-8 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                Similar Events You May Like
              </h3>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {similarEvents.slice(0, 3).map((similarEvent, index) => (
                  <motion.div
                    key={similarEvent.slug || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                  >
                    <ConferenceCard
                      item={similarEvent}
                      onClick={() =>
                        navigate(`/event-details/${similarEvent.slug}`)
                      }
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Full Screen Image Modal */}
      <AnimatePresence>
        {isImageModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/95"
              onClick={() => setIsImageModalOpen(false)}
            >
              {/* Close Button */}
              <motion.button
                onClick={() => setIsImageModalOpen(false)}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="flex absolute top-4 right-4 justify-center items-center w-12 h-12 text-white rounded-full border backdrop-blur-md transition-all z-60 bg-black/50 border-white/20 hover:bg-black/70 hover:border-white/40"
              >
                <X size={24} />
              </motion.button>

              {/* Image Container */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={allImages[currentImageIndex]}
                  alt={event.event_title || "Event"}
                  className="max-w-full max-h-[95vh] object-contain rounded-lg shadow-2xl"
                />

                {/* Navigation Arrows - Only if multiple images */}
                {allImages.length > 1 && (
                  <>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="flex absolute left-4 top-1/2 justify-center items-center w-14 h-14 text-white rounded-full border backdrop-blur-md transition-all bg-black/50 border-white/20 hover:bg-black/70 hover:border-white/40"
                    >
                      <ChevronLeft size={28} />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="flex absolute right-4 top-1/2 justify-center items-center w-14 h-14 text-white rounded-full border backdrop-blur-md transition-all bg-black/50 border-white/20 hover:bg-black/70 hover:border-white/40"
                    >
                      <ChevronRight size={28} />
                    </motion.button>

                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 px-4 py-2 text-sm font-semibold text-white rounded-full border backdrop-blur-md -translate-x-1/2 bg-black/50 border-white/20">
                      {currentImageIndex + 1} / {allImages.length}
                    </div>

                    {/* Image Dots */}
                    <div className="flex absolute bottom-16 left-1/2 gap-2 -translate-x-1/2">
                      {allImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(index);
                          }}
                          className={`h-2 rounded-full transition-all ${
                            currentImageIndex === index
                              ? "w-8 bg-white"
                              : "w-2 bg-white/50 hover:bg-white/75"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Footer />

      {/* Registration Drawer - For Conferences */}
      {isConference && (
        <RegistrationDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          event={event}
          eventDate={eventDate}
          eventTime={eventTime}
          isConference={isConference}
          selectedTicket={selectedTicket}
          setSelectedTicket={setSelectedTicket}
          onContinue={(ticket) => {
            try {
              sessionStorage.setItem(
                `conferenceRegistrationTicket_${slug}`,
                JSON.stringify(ticket)
              );
            } catch {
              // ignore storage failures
            }
            setIsDrawerOpen(false);
            navigate(`/event-details/${slug}/register`, {
              state: { selectedTicket: ticket },
            });
          }}
        />
      )}
    </div>
  );
}
